import { useState } from 'react';
import type { FC, MouseEvent } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Archive,
  Code2,
  Eye,
  File,
  FileAudio,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  LoaderIcon,
  Presentation,
  X,
} from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Attachment } from '@/types/chat-message';

import { AttachmentPreviewDialog } from './attachment-preview-dialog';

interface PreviewAttachmentProps {
  attachment: Attachment;
  isUploading?: boolean;
  onRemove?: (attachment: Attachment) => void;
  showRemoveButton?: boolean;
  isRemoving?: boolean;
  variant?: 'pill' | 'composer';
}

interface FileTypeConfig {
  icon: LucideIcon;
  shortLabel?: string;
  bgColor: string;
  textColor: string;
  label: string;
}

function getFileExtension(url: string): string {
  const cleanUrl = url.split('?')[0];
  const parts = cleanUrl.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

function getFileTypeConfig(contentType?: string, url?: string, name?: string): FileTypeConfig {
  const ext = url ? getFileExtension(url) : (name ? getFileExtension(name) : '');

  if (contentType?.includes('word') || ['doc', 'docx'].includes(ext)) {
    return {
      icon: FileText,
      shortLabel: 'DOC',
      bgColor: 'bg-blue-500',
      textColor: 'text-white',
      label: 'Document',
    };
  }

  if (contentType?.includes('pdf') || ext === 'pdf') {
    return {
      icon: FileText,
      shortLabel: 'PDF',
      bgColor: 'bg-red-500',
      textColor: 'text-white',
      label: 'PDF',
    };
  }

  if (contentType?.includes('spreadsheet') || contentType?.includes('excel') || ['xls', 'xlsx', 'csv'].includes(ext)) {
    return {
      icon: FileSpreadsheet,
      shortLabel: 'XLS',
      bgColor: 'bg-green-600',
      textColor: 'text-white',
      label: 'Spreadsheet',
    };
  }

  if (contentType?.includes('presentation') || ['ppt', 'pptx'].includes(ext)) {
    return {
      icon: Presentation,
      shortLabel: 'PPT',
      bgColor: 'bg-orange-500',
      textColor: 'text-white',
      label: 'Presentation',
    };
  }

  if (contentType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) {
    return {
      icon: FileImage,
      bgColor: 'bg-purple-500',
      textColor: 'text-white',
      label: 'Image',
    };
  }

  if (contentType?.startsWith('video/') || ['mp4', 'avi', 'mov', 'wmv', 'webm', 'mkv'].includes(ext)) {
    return {
      icon: FileVideo,
      bgColor: 'bg-pink-500',
      textColor: 'text-white',
      label: 'Video',
    };
  }

  if (contentType?.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'].includes(ext)) {
    return {
      icon: FileAudio,
      bgColor: 'bg-indigo-500',
      textColor: 'text-white',
      label: 'Audio',
    };
  }

  if (contentType?.includes('zip') || contentType?.includes('archive') || ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
    return {
      icon: Archive,
      bgColor: 'bg-yellow-600',
      textColor: 'text-white',
      label: 'Archive',
    };
  }

  if (['js', 'jsx', 'ts', 'tsx', 'json', 'html', 'css', 'py', 'java', 'cpp', 'go', 'rs'].includes(ext)) {
    return {
      icon: Code2,
      bgColor: 'bg-gray-700',
      textColor: 'text-white',
      label: 'Code',
    };
  }

  if (contentType?.includes('text') || ['txt', 'rtf', 'md'].includes(ext)) {
    return {
      icon: FileText,
      shortLabel: 'TXT',
      bgColor: 'bg-gray-500',
      textColor: 'text-white',
      label: 'Text',
    };
  }

  return {
    icon: File,
    bgColor: 'bg-gray-400',
    textColor: 'text-white',
    label: 'File',
  };
}

function formatFileSize(bytes?: number): string {
  if (!bytes || bytes === 0) return '';

  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(size < 10 ? 2 : 1)} ${units[unitIndex]}`;
}

function isImageFile(contentType?: string, url?: string, name?: string): boolean {
  if (contentType?.startsWith('image/')) return true;

  const source = url || name;
  if (!source) return false;

  const ext = getFileExtension(source);
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext);
}

export const PreviewAttachment: FC<PreviewAttachmentProps> = ({
  attachment,
  isUploading = false,
  onRemove,
  showRemoveButton = true,
  isRemoving = false,
  variant = 'pill',
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { name, url, contentType, size } = attachment;
  const isImage = isImageFile(contentType, url, name);
  const fileConfig = getFileTypeConfig(contentType, url, name);
  const FileIcon = fileConfig.icon;
  const formattedSize = formatFileSize(size);

  const handleRemove = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (!isRemoving) {
      onRemove?.(attachment);
    }
  };

  const handlePreview = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (!isUploading && !isRemoving && url) {
      setIsPreviewOpen(true);
    }
  };

  const canPreview = !isUploading && !isRemoving && Boolean(url);

  if (variant === 'composer') {
    return (
      <>
        <div className="relative inline-flex">
          <div
            role={canPreview ? 'button' : undefined}
            tabIndex={canPreview ? 0 : undefined}
            onClick={canPreview ? handlePreview : undefined}
            onKeyDown={canPreview ? (event) => event.key === 'Enter' && handlePreview(event as unknown as MouseEvent) : undefined}
            className={cn(
              'relative flex h-[88px] w-[92px] flex-col overflow-hidden rounded-lg border border-border/70 bg-background shadow-sm transition-colors',
              canPreview && 'cursor-pointer hover:border-primary/50 hover:bg-muted/40',
            )}
          >
            <div className="flex h-14 items-center justify-center bg-muted/50">
              {isImage && url ? (
                <img
                  src={url}
                  alt={name || 'Image'}
                  className="size-full object-cover"
                  onError={(event) => {
                    (event.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <div className={cn('flex size-10 items-center justify-center rounded-md', fileConfig.bgColor)}>
                  {fileConfig.shortLabel ? (
                    <span className={cn('text-[10px] font-bold leading-none', fileConfig.textColor)}>
                      {fileConfig.shortLabel}
                    </span>
                  ) : (
                    <FileIcon className={cn('size-5', fileConfig.textColor)} />
                  )}
                </div>
              )}

              {(isUploading || isRemoving) && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm">
                  <LoaderIcon className="size-4 animate-spin text-primary" />
                </div>
              )}
            </div>

            <div className="min-w-0 px-2 py-1">
              <p className="truncate text-[11px] font-medium leading-4 text-foreground">
                {name || 'Unnamed file'}
              </p>
              <p className="truncate text-[10px] leading-3 text-muted-foreground">
                {isUploading ? 'Uploading...' : isRemoving ? 'Removing...' : fileConfig.label}
              </p>
            </div>
          </div>

          {!isUploading && !isRemoving && onRemove && showRemoveButton && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={handleRemove}
                  className="absolute -right-1.5 -top-1.5 z-10 flex size-5 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm transition-colors hover:border-destructive hover:bg-destructive hover:text-destructive-foreground"
                  aria-label={`Remove ${name || 'file'}`}
                >
                  <X className="size-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Remove</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        <AttachmentPreviewDialog
          attachment={attachment}
          open={isPreviewOpen}
          onOpenChange={setIsPreviewOpen}
        />
      </>
    );
  }

  return (
    <>
      <div className="group relative inline-flex">
        <div
          role={canPreview ? 'button' : undefined}
          tabIndex={canPreview ? 0 : undefined}
          onClick={canPreview ? handlePreview : undefined}
          onKeyDown={canPreview ? (event) => event.key === 'Enter' && handlePreview(event as unknown as MouseEvent) : undefined}
          className={cn(
            'flex max-w-[280px] items-center gap-2.5 rounded-xl border border-border/60 bg-card px-3 py-2 transition-colors',
            canPreview && 'cursor-pointer hover:border-border hover:bg-muted/50',
          )}
        >
          <div className="relative shrink-0">
            {isImage && url ? (
              <div className="size-9 overflow-hidden rounded-md bg-muted">
                <img
                  src={url}
                  alt={name || 'Image'}
                  className="size-full object-cover"
                  onError={(event) => {
                    (event.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            ) : (
              <div className={cn('flex size-9 items-center justify-center rounded-md', fileConfig.bgColor)}>
                {fileConfig.shortLabel ? (
                  <span className={cn('text-xs font-bold', fileConfig.textColor)}>
                    {fileConfig.shortLabel}
                  </span>
                ) : (
                  <FileIcon className={cn('size-4', fileConfig.textColor)} />
                )}
              </div>
            )}

            {(isUploading || isRemoving) && (
              <div className="absolute inset-0 flex items-center justify-center rounded-md bg-background/80 backdrop-blur-sm">
                <LoaderIcon className="size-4 animate-spin text-primary" />
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-foreground">
              {name || 'Unnamed file'}
            </p>
            <p className="text-xs text-muted-foreground">
              {isUploading ? (
                'Uploading...'
              ) : isRemoving ? (
                'Removing...'
              ) : (
                <>
                  {fileConfig.label}
                  {formattedSize && ` - ${formattedSize}`}
                </>
              )}
            </p>
          </div>

          {canPreview && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={handlePreview}
                  className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label={`Preview ${name || 'file'}`}
                >
                  <Eye className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Preview</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {!isUploading && !isRemoving && onRemove && showRemoveButton && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleRemove}
                className="absolute -right-1.5 -top-1.5 z-10 flex size-5 items-center justify-center rounded-full border border-border bg-muted text-muted-foreground opacity-0 shadow-sm transition-all duration-200 hover:border-destructive hover:bg-destructive hover:text-destructive-foreground group-hover:opacity-100 focus:opacity-100"
                aria-label={`Remove ${name || 'file'}`}
              >
                <X className="size-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Remove</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      <AttachmentPreviewDialog
        attachment={attachment}
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
      />
    </>
  );
};
